if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        const stripeToggle = this.querySelector('.everlio-stripe-toggle');
        if (stripeToggle) stripeToggle.disabled = false;
        const glueToggle = this.querySelector('.everlio-glue-toggle');
        if (glueToggle) glueToggle.disabled = false;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const customText = this.form.querySelector('[data-everlio-custom-text]');
        if (customText) {
          customText.value = customText.value.trim();
          if (!this.form.reportValidity()) return;
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        /* ── Bundle multi-variant mode ── */
        const isBundle = window.__erdensaiBundleMode === 'multi'
                      && Array.isArray(window.__erdensaiBundleItems)
                      && window.__erdensaiBundleItems.length > 0;

        if (isBundle) {
          this._submitBundle(evt);
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const addonVariants = this.getAddonVariants();
        if (addonVariants.length) {
          const entries = Array.from(formData.entries());
          const existingItems = entries.filter(([key]) => /^items\[\d+\]\[id\]$/.test(key));
          let stripeIndex = 1;
          let stripeQuantity = Number(formData.get('quantity') || 1);
          if (existingItems.length) {
            const indexes = existingItems.map(([key]) => Number(key.match(/^items\[(\d+)\]/)[1]));
            stripeIndex = Math.max(...indexes) + 1;
            stripeQuantity = indexes.reduce((sum, index) => sum + Number(formData.get('items[' + index + '][quantity]') || 1), 0);
          } else {
            entries.forEach(([key, value]) => {
              if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) {
                const itemKey = key.startsWith('properties[') ? 'items[0][properties]' + key.slice(10) : 'items[0][' + key + ']';
                formData.delete(key);
                formData.append(itemKey, value);
              }
            });
            if (!formData.has('items[0][quantity]')) formData.append('items[0][quantity]', String(stripeQuantity));
          }
          addonVariants.forEach((variantId, offset) => {
            formData.append('items[' + (stripeIndex + offset) + '][id]', variantId);
            formData.append('items[' + (stripeIndex + offset) + '][quantity]', String(stripeQuantity));
          });
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: this.variantIdInput.value,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: this.variantIdInput.value,
                cartData: response,
              })).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Unable to add items. Please check your cart before trying again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /**
       * Bundle multi-variant add-to-cart.
       * Uses Shopify's /cart/add.js with { items: [...] } JSON payload
       * so each variant is added with its own quantity.
       */
      _submitBundle(evt) {
        const items = window.__erdensaiBundleItems.map(function(it) {
          const item = { id: it.id, quantity: it.quantity };
          if (it.properties && typeof it.properties === 'object') {
            item.properties = it.properties;
          }
          return item;
        });

        const addonQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 1), 0);
        this.getAddonVariants().forEach(variantId => {
          items.push({ id: Number(variantId), quantity: addonQuantity });
        });
        const sectionsToFetch = this.cart
          ? this.cart.getSectionsToRender().map((s) => s.id)
          : [];

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        const body = { items: items };
        if (sectionsToFetch.length) {
          body.sections = sectionsToFetch;
          body.sections_url = window.location.pathname;
        }

        console.log('[Bundle] Adding multiple variants:', JSON.stringify(body));

        fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: items[0].id,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: items[0].id,
                cartData: response,
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => { this.cart.renderContents(response); });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('[Bundle] Add to cart error:', e);
            this.handleErrorMessage('Bağlantı hatası, lütfen tekrar deneyin');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getAddonVariants() {
        const variants = [];
        const stripe = this.getStripeVariant();
        if (stripe) variants.push(stripe);
        const glue = this.querySelector('.everlio-glue-toggle');
        if (glue && glue.checked && glue.dataset.glueVariant) variants.push(glue.dataset.glueVariant);
        return variants;
      }

      getStripeVariant() {
        const stripe = this.querySelector('[data-everlio-stripe]');
        if (!stripe || !stripe.querySelector('.everlio-stripe-toggle').checked) return null;
        return stripe.querySelector('[data-stripe-variant]').value;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
